import { WidgetHeaderBar } from './WidgetHeaderBar';
import { DragButton } from './DragButton';
import { EditModeWidgetTile } from './WidgetTitle';
import { EditModeOptionsButton } from './EditModeOptionsButton';
import { EditIcon, Icon, IconName } from '@ardoq/icons';
import { ButtonSize, GhostButton, TertiaryButton } from '@ardoq/button';
import { WIDGET_EDIT_BUTTON_ID } from '../../consts';
import { HeaderBarButtonGroup } from './HeaderBarButtonGroup';
import {
  WithPopover,
  PlainTextPopover,
  WithUncontrolledPopover,
} from '@ardoq/popovers';
import {
  ActiveDashboardFiltersIndicator,
  getActiveDashboardFiltersIndicatorProps,
} from './ActiveDashboardFiltersIndicator';
import { WidgetLinkedDashboardFilter } from '../types';
import { isEmpty } from 'lodash';
import { Switch } from '@ardoq/forms';
import { Text } from '@ardoq/typography';
import { FlexBox } from '@ardoq/layout';
import {
  HighlightedText,
  HighlightedTextConfig,
} from '@ardoq/basic-components';
import { colors } from '@ardoq/design-tokens';
import { AlertMessagesContainer } from './WidgetCard';
import { type I18nLocale } from '@ardoq/i18n';

type CommonEditModeHeaderBarCommands = {
  selectWidget: VoidFunction;
  copyWidget: VoidFunction;
  deleteWidget: VoidFunction;
};

export type AlertMessageProps = {
  iconName: IconName;
  messages: HighlightedTextConfig[];
};
export type NoteWidgetHeaderBarProps = {
  commands: CommonEditModeHeaderBarCommands;
  isSaving: boolean;
  editButtonTooltipText?: string;
  i18nLocale: I18nLocale;
};

export type LinkModeChartWidgetHeaderBarButtonsProps = {
  linkToggle: {
    isDisabled: boolean;
    isChecked: boolean;
    label: string;
    name: string;
    tooltip?: string;
    onChange: () => void;
  } | null;
  linkButton: {
    text: string;
    icon?: IconName;
    onClick: () => void;
  } | null;
};

type LinkModeChartWidgetHeaderBarProps = {
  isInLinkDashboardFiltersMode: boolean;
  linkModeButtons: LinkModeChartWidgetHeaderBarButtonsProps;
};

export type EditModeChartWidgetHeaderBarProps = {
  canEditWidget: boolean;
  cannotEditMessage?: string;
  editButtonTooltipText?: string;
  isSaving: boolean;
  activeLinkedDashboardFilters: WidgetLinkedDashboardFilter[];
  linkModeProps: LinkModeChartWidgetHeaderBarProps;
  alertMessages?: AlertMessageProps[];
  name?: string;
  isEditDisabled: boolean;
  commands: CommonEditModeHeaderBarCommands & {
    editWidgetLinkModeDashboardFilterBinding: VoidFunction;
    unlinkWidgetWithDashboardFilter: VoidFunction | undefined;
    linkWidgetWithDashboardFilter: VoidFunction | undefined;
  };
  i18nLocale: I18nLocale;
};

const AlertMessage = ({ iconName, messages }: AlertMessageProps) => (
  <WithPopover
    content={() => (
      <FlexBox gap="xsmall" flexDirection="column">
        {messages.map((message, index) => (
          <HighlightedText key={index} {...message} />
        ))}
      </FlexBox>
    )}
  >
    <Icon iconName={iconName} color={colors.iconWarning} />
  </WithPopover>
);
export const EditModeChartWidgetHeaderBar = ({
  name,
  isEditDisabled,
  canEditWidget,
  activeLinkedDashboardFilters,
  linkModeProps: { isInLinkDashboardFiltersMode, linkModeButtons },
  cannotEditMessage,
  commands,
  editButtonTooltipText,
  isSaving,
  alertMessages,
  i18nLocale,
}: EditModeChartWidgetHeaderBarProps) => {
  return (
    <WidgetHeaderBar>
      {!isInLinkDashboardFiltersMode && (
        <DragButton isSaving={isSaving} i18nLocale={i18nLocale} />
      )}
      {name && <EditModeWidgetTile>{name}</EditModeWidgetTile>}
      <HeaderBarButtonGroup>
        {!isInLinkDashboardFiltersMode && (
          <>
            {alertMessages?.length ? (
              <AlertMessagesContainer
                gap="small"
                className="show-only-on-hover"
              >
                {alertMessages.map((alert, index) => (
                  <AlertMessage key={index} {...alert} />
                ))}
              </AlertMessagesContainer>
            ) : null}
            <WithUncontrolledPopover
              content={
                (!canEditWidget || isEditDisabled) && cannotEditMessage ? (
                  <PlainTextPopover>{cannotEditMessage}</PlainTextPopover>
                ) : null
              }
            >
              <FlexBox className="show-only-on-hover">
                <GhostButton
                  dataTestId={WIDGET_EDIT_BUTTON_ID}
                  data-tooltip-text={editButtonTooltipText}
                  onClick={commands.selectWidget}
                  isDisabled={!canEditWidget || isEditDisabled}
                >
                  <EditIcon />
                </GhostButton>
              </FlexBox>
            </WithUncontrolledPopover>
            <EditModeOptionsButton
              canEdit={canEditWidget}
              copyWidget={commands.copyWidget}
              deleteWidget={commands.deleteWidget}
              i18nLocale={i18nLocale}
            />
            {!isEmpty(activeLinkedDashboardFilters) && (
              <ActiveDashboardFiltersIndicator
                {...getActiveDashboardFiltersIndicatorProps(
                  activeLinkedDashboardFilters,
                  i18nLocale
                )}
              />
            )}
          </>
        )}
        {isInLinkDashboardFiltersMode && (
          <>
            {linkModeButtons.linkToggle && (
              <FlexBox
                data-tooltip-text={linkModeButtons.linkToggle.tooltip}
                align="center"
                justify="center"
                gap="small"
              >
                <Text>{linkModeButtons.linkToggle.label}</Text>
                <Switch
                  {...linkModeButtons.linkToggle}
                  label={undefined}
                  alignItemsCenter={true}
                />
              </FlexBox>
            )}
            {linkModeButtons.linkButton && (
              <TertiaryButton
                onClick={linkModeButtons.linkButton.onClick}
                buttonSize={ButtonSize.SMALL}
              >
                {linkModeButtons.linkButton.icon && (
                  <Icon iconName={linkModeButtons.linkButton.icon} />
                )}
                {linkModeButtons.linkButton.text}
              </TertiaryButton>
            )}
          </>
        )}
      </HeaderBarButtonGroup>
    </WidgetHeaderBar>
  );
};

export const NoteWidgetEditModeHeaderBar = ({
  isSaving,
  editButtonTooltipText,
  commands,
  i18nLocale,
}: NoteWidgetHeaderBarProps) => (
  <WidgetHeaderBar>
    <DragButton isSaving={isSaving} i18nLocale={i18nLocale} />
    <HeaderBarButtonGroup>
      <GhostButton
        dataTestId={WIDGET_EDIT_BUTTON_ID}
        data-tooltip-text={editButtonTooltipText}
        onClick={commands.selectWidget}
      >
        <EditIcon />
      </GhostButton>
      <EditModeOptionsButton
        canEdit
        copyWidget={commands.copyWidget}
        deleteWidget={commands.deleteWidget}
        i18nLocale={i18nLocale}
      />
    </HeaderBarButtonGroup>
  </WidgetHeaderBar>
);
